import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api";

interface Props {
  name: string;
  storageKey: string;
  residentId?: number;  // optional — only passed in edit flow
  onClose: () => void;
}

export default function BiometricEnrollment({ name, storageKey, residentId, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const tickRef = useRef<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [ready, setReady] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);

  const stopCamera = (video: HTMLVideoElement | null) => {
    if (!video) return;
    const stream = video.srcObject as MediaStream | null;
    if (stream) for (const track of stream.getTracks()) { try { track.stop(); } catch {} }
    try {
      video.pause();
      video.srcObject = null;
      (video as any).load?.();
    } catch {}
  };

  // one-time init: reset backend, open camera
  useEffect(() => {
    let canceled = false;

    // legacy cleanup only
    try { localStorage.removeItem(storageKey); } catch {}

    (async () => {
      try {
        // server returns { status: "ok", enrollment_id }
        const res = await apiFetch("/biometric_enrollment/start", { method: "POST" });
        const data = await res.json();
        if (data?.status === "ok" && data?.enrollment_id) {
          setEnrollmentId(data.enrollment_id);
          setReady(true);
        } else {
          throw new Error("start returned non-ok");
        }
      } catch (e) {
        console.error("start/reset failed", e);
        alert("Unable to start biometric enrollment.");
      }
    })();

    navigator.mediaDevices.getUserMedia({
    video: { facingMode: "environment" }  // outward/rear camera
    })
      .then((stream) => { if (!canceled && videoRef.current) videoRef.current.srcObject = stream; })
      .catch(() => alert("Unable to access webcam"));

    return () => {
      canceled = true;
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      stopCamera(videoRef.current);
    };
  }, [storageKey]);

  // frame upload loop
  useEffect(() => {
    if (!ready || tickRef.current || !videoRef.current || !enrollmentId) return;

    tickRef.current = window.setInterval(async () => {
      if (!videoRef.current || isComplete) return;

      const v = videoRef.current;
      if (!v.videoWidth || !v.videoHeight) return;

      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(v, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg"));
      if (!blob) return;

      const formData = new FormData();
      formData.append("frame", blob, "frame.jpg");
      formData.append("enrollment_id", enrollmentId); // ✅ required by backend

      try {
        const res = await apiFetch("/biometric_enrollment/frame", { method: "POST", body: formData });
        const data = await res.json();

        // progress: { accepted, target }
        if (data?.progress && typeof data.progress.accepted === "number" && typeof data.progress.target === "number") {
          const frac = Math.max(0, Math.min(1, data.progress.accepted / Math.max(1, data.progress.target)));
          setProgress((prev) => Math.max(prev, frac));

          if (frac >= 1.0 && !isComplete) {
            setIsComplete(true);

            if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }

            try {
              const finalizeRes = await apiFetch("/biometric_enrollment/complete", {
                method: "POST",
                body: new URLSearchParams({ enrollment_id: enrollmentId, name }),
              });

              let finalizeData: any = null;
              try {
                finalizeData = await finalizeRes.json();
              } catch {
                // If server ever returns empty body
                finalizeData = {};
              }

              // Debug once so we can see the exact payload in the browser console
              console.log("[enroll] finalize response", finalizeRes.status, finalizeData);

              // Accept a few success shapes:
              const statusVal = String(finalizeData?.status || "").toLowerCase();
              const successByStatus = statusVal === "ok" || statusVal === "complete";
              const successByPayload =
                "final_path" in (finalizeData ?? {}) ||
                "embedding_path" in (finalizeData ?? {}) ||
                "embeddings_saved" in (finalizeData ?? {});

              if (finalizeRes.ok && (successByStatus || successByPayload)) {
                if (residentId) {
                    // Edit flow — commit directly to DB
                    try {
                        const commitRes = await apiFetch("/biometric_enrollment/commit", {
                            method: "POST",
                            body: new URLSearchParams({
                                resident_id: String(residentId),
                                enrollment_id: enrollmentId,
                            }),
                        });
                        const commitData = await commitRes.json();

                        if (commitRes.ok && commitData?.status === "ok") {
                            // Fire biometric-ready AFTER successful DB commit
                            window.dispatchEvent(
                                new CustomEvent("biometric-ready", {
                                    detail: { name, enrollment_id: enrollmentId },
                                })
                            );
                            alert(`✅ Enrollment complete — ${commitData.embeddings} embeddings saved.`);
                        } else if (commitData?.status === "duplicate") {
                            alert(`⚠️ Duplicate detected: ${commitData.detail}`);
                        } else {
                            alert(`⚠️ Commit failed: ${commitData?.message || "unknown error"}`);
                        }
                    } catch (err) {
                        console.error("Commit error:", err);
                        alert("❌ Could not commit enrollment. Check backend logs.");
                    }
                } else {
                    // Add flow — parent handles commit via enrollment_id
                    window.dispatchEvent(
                        new CustomEvent("biometric-ready", {
                            detail: { name, enrollment_id: enrollmentId },
                        })
                    );
                    const saved = Number(finalizeData?.embeddings_saved ?? 0);
                    alert(`✅ Enrollment complete — ${saved} embeddings saved.`);
                }
              }
            } catch (err) {
              console.error("Finalize error:", err);
              alert("❌ Could not finalize enrollment. Check backend logs.");
            }


            stopCamera(videoRef.current);
            onClose();
          }
        }
      } catch (err) {
        console.error("Upload failed", err);
      }
    }, 800);

    return () => {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };
  }, [ready, isComplete, enrollmentId]);

  const handleCancel = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    stopCamera(videoRef.current);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-[500px] text-center">
        <h2 className="text-xl font-semibold mb-4">Biometric Enrollment</h2>
        <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg mb-4" />
        <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
          <div className="bg-green-500 h-3 rounded-full transition-all duration-300" style={{ width: `${Math.min(progress, 1) * 100}%` }} />
        </div>
        <p>{Math.round(Math.min(progress, 1) * 100)}% Complete</p>
        <button onClick={handleCancel} className="mt-4 bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md">
          Cancel
        </button>
      </div>
    </div>
  );
}
